import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function AdminAuthSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteKey, setSiteKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [editSecret, setEditSecret] = useState(false);
  const [requireRegister, setRequireRegister] = useState(true);
  const [requireLogin, setRequireLogin] = useState(true);
  const [uaFilterMode, setUaFilterMode] = useState("auto"); // "auto" | "on" | "off"
  const [uaSaving, setUaSaving] = useState(false);
  const [disposableText, setDisposableText] = useState("");
  const [events, setEvents] = useState(null);
  const [eventKind, setEventKind] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/auth-settings");
      setSettings(data);
      setSiteKey(data.turnstile_site_key || "");
      setRequireRegister(!!data.captcha_required_register);
      setRequireLogin(!!data.captcha_required_login);
      if (data.ua_filter_enabled === true) setUaFilterMode("on");
      else if (data.ua_filter_enabled === false) setUaFilterMode("off");
      else setUaFilterMode("auto");
      setDisposableText((data.disposable_extra || []).join("\n"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar config");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const q = eventKind ? `?kind=${encodeURIComponent(eventKind)}` : "";
      const { data } = await api.get(`/admin/security-events${q}`);
      setEvents(data);
    } catch (e) {
      // ignorar — stats opcionales
    }
  }, [eventKind]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  /** Auto-save instantáneo del UA filter al cambiar el modo.
   *  Evita el bug donde el usuario clickeaba el toggle pero olvidaba el botón
   *  "Guardar configuración" al final de la página y al refrescar perdía el cambio.
   *  Memoizado con useCallback para evitar recreación en cada render.
   */
  const setAndSaveUaFilter = useCallback(async (mode) => {
    const prev = uaFilterMode;
    setUaFilterMode(mode);
    setUaSaving(true);
    try {
      if (mode === "auto") {
        await api.delete("/admin/auth-settings/ua-filter-override");
      } else {
        await api.patch("/admin/auth-settings", { ua_filter_enabled: mode === "on" });
      }
      const { data } = await api.get("/admin/auth-settings");
      setSettings(data);
      toast.success(
        mode === "auto"
          ? "UA filter: vuelto a modo automático"
          : `UA filter ${mode === "on" ? "activado" : "desactivado"} (override DB)`
      );
    } catch (e) {
      setUaFilterMode(prev);
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar UA filter");
    } finally {
      setUaSaving(false);
    }
  }, [uaFilterMode]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        turnstile_site_key: siteKey,
        captcha_required_register: requireRegister,
        captcha_required_login: requireLogin,
        disposable_extra: disposableText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
      };
      if (editSecret) {
        payload.turnstile_secret_key = secretKey;
      }
      // UA filter NO se guarda aquí — usa su propio auto-save instantáneo
      // (`setAndSaveUaFilter`) para evitar el bug donde el usuario clickeaba
      // el toggle sin recordar guardar la configuración general.
      const { data } = await api.patch("/admin/auth-settings", payload);
      setSettings(data);
      setEditSecret(false);
      setSecretKey("");
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  }, [siteKey, requireRegister, requireLogin, disposableText, editSecret, secretKey]);

  const clearSecret = useCallback(async () => {
    setSaving(true);
    try {
      await api.patch("/admin/auth-settings", { turnstile_secret_key: "" });
      toast.success("Secret key eliminada");
      await load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally {
      setSaving(false);
    }
  }, [load]);

  if (loading) return <div className="container-prose py-10">Cargando…</div>;

  const captchaActive = settings?.has_secret_key && !!settings?.turnstile_site_key;

  return (
    <div className="container-prose py-10" data-testid="admin-auth-settings-page">
      <div className="mb-6">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Admin</Link>
        <h1 className="font-serif text-3xl text-gray-900 mt-2">Seguridad de autenticación</h1>
        <p className="text-sm text-gray-600 mt-1">
          Configura CAPTCHA, dominios desechables bloqueados y revisa eventos de seguridad anti-bot.
        </p>
      </div>

      {/* STATUS PILL */}
      <div className={`border p-4 mb-6 ${captchaActive ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="captcha-status">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${captchaActive ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
          <p className="text-sm">
            <strong>CAPTCHA {captchaActive ? "ACTIVO" : "INACTIVO"}:</strong>{" "}
            {captchaActive
              ? "Las protecciones anti-bot vía Cloudflare Turnstile están funcionando."
              : "No hay site_key + secret_key configuradas. El sistema usa solo honeypot + rate-limit + bloqueo de emails desechables."}
          </p>
        </div>
      </div>

      {/* TURNSTILE CONFIG */}
      <div className="border border-gray-200 bg-white mb-6" data-testid="turnstile-config-section">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-serif text-lg">Cloudflare Turnstile</h2>
          <p className="text-xs text-gray-500 mt-1">
            Obtén tus claves en{" "}
            <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              dash.cloudflare.com/turnstile ↗
            </a>
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <Label htmlFor="site-key">Site Key (pública)</Label>
            <Input
              id="site-key"
              value={siteKey}
              onChange={(e) => setSiteKey(e.target.value)}
              placeholder="0x4AAAAAAA..."
              className="font-mono text-xs"
              data-testid="site-key-input"
            />
          </div>

          <div>
            <Label htmlFor="secret-key">Secret Key (privada — encriptada en DB)</Label>
            {!editSecret ? (
              <div className="flex items-center gap-2">
                <code className="font-mono text-xs flex-1 bg-gray-50 border border-gray-200 px-3 py-2" data-testid="secret-key-masked">
                  {settings?.turnstile_secret_key_masked || "(no configurada)"}
                </code>
                <Button size="sm" variant="outline" onClick={() => { setEditSecret(true); setSecretKey(""); }} className="rounded-sm" data-testid="edit-secret-btn">
                  {settings?.has_secret_key ? "Cambiar" : "Configurar"}
                </Button>
                {settings?.has_secret_key && (
                  <Button size="sm" variant="outline" onClick={clearSecret} disabled={saving} className="rounded-sm text-rose-600 hover:bg-rose-50" data-testid="clear-secret-btn">
                    Eliminar
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  id="secret-key"
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder="0x4AAAAAAA..."
                  className="font-mono text-xs flex-1"
                  autoFocus
                  data-testid="secret-key-input"
                />
                <Button size="sm" variant="outline" onClick={() => { setEditSecret(false); setSecretKey(""); }} className="rounded-sm" data-testid="cancel-secret-edit-btn">
                  Cancelar
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <Label>Requerir en registro</Label>
              <Switch checked={requireRegister} onCheckedChange={setRequireRegister} data-testid="toggle-register" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Requerir en login</Label>
              <Switch checked={requireLogin} onCheckedChange={setRequireLogin} data-testid="toggle-login" />
            </div>
          </div>
        </div>
      </div>

      {/* UA FILTER (Capa 7 anti-bot) */}
      <div className="border border-gray-200 bg-white mb-6" data-testid="ua-filter-section">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-serif text-lg">Filtro de User-Agent (Capa 7 anti-bot)</h2>
          <p className="text-xs text-gray-500 mt-1">
            Bloquea clientes no-navegador (curl, python-requests, scrapy, headless chrome, etc.) en endpoints de auth.
            <br />
            <strong>Modo &quot;auto&quot;</strong>: activo solo si la app corre en producción (PRODUCTION_URL set). Override aquí si necesitas forzar ON u OFF.
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="ua-filter-mode-radiogroup">
            {[
              { value: "auto", label: "Auto", testid: "ua-filter-auto" },
              { value: "on", label: "Forzar ON", testid: "ua-filter-on" },
              { value: "off", label: "Forzar OFF", testid: "ua-filter-off" },
            ].map((opt) => (
              <Button
                key={opt.value}
                type="button"
                variant={uaFilterMode === opt.value ? "default" : "outline"}
                onClick={() => setAndSaveUaFilter(opt.value)}
                disabled={uaSaving}
                className="rounded-sm h-9 text-xs"
                data-testid={opt.testid}
              >
                {opt.label}
              </Button>
            ))}
            {uaSaving && (
              <span className="self-center text-[11px] text-gray-500" data-testid="ua-filter-saving">
                Guardando…
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500" data-testid="ua-filter-current-state">
            Estado en backend ahora:{" "}
            <strong>
              {settings?.ua_filter_enabled === true ? "ON (override DB)"
                : settings?.ua_filter_enabled === false ? "OFF (override DB)"
                : "auto (sin override)"}
            </strong>
          </p>
        </div>
      </div>

      {/* DISPOSABLE EMAILS */}
      <div className="border border-gray-200 bg-white mb-6" data-testid="disposable-section">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-serif text-lg">Dominios desechables adicionales</h2>
          <p className="text-xs text-gray-500 mt-1">
            Hay {settings?.disposable_extra?.length || 0} dominios extra. Ya bloqueamos por defecto mailinator, tempmail, yopmail, etc.
          </p>
        </div>
        <div className="p-4">
          <Label htmlFor="disposable">Un dominio por línea (sin @)</Label>
          <textarea
            id="disposable"
            value={disposableText}
            onChange={(e) => setDisposableText(e.target.value)}
            placeholder="ejemplo-temporal.com&#10;otro-dominio.io"
            rows={6}
            className="w-full mt-1 border border-gray-300 rounded-sm p-2 font-mono text-xs"
            data-testid="disposable-textarea"
          />
        </div>
      </div>

      <div className="flex justify-end mb-10">
        <Button onClick={save} disabled={saving} className="rounded-sm h-11 px-8" data-testid="save-settings-btn">
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      {/* SECURITY EVENTS */}
      <div className="border border-gray-200 bg-white" data-testid="security-events-section">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="font-serif text-lg">Eventos de seguridad</h2>
            <p className="text-xs text-gray-500 mt-1">Capa anti-bot: bots detectados y bloqueados.</p>
          </div>
          <select className="border border-gray-300 px-2 h-9 text-xs" value={eventKind} onChange={(e) => setEventKind(e.target.value)} data-testid="event-kind-filter">
            <option value="">Todos</option>
            <option value="honeypot_filled">Honeypot</option>
            <option value="captcha_failed">CAPTCHA fallido</option>
            <option value="bad_ua">User-Agent sospechoso</option>
            <option value="too_fast">Demasiado rápido</option>
            <option value="disposable_email">Email desechable</option>
          </select>
        </div>

        {events?.by_kind && events.by_kind.length > 0 && (
          <div className="p-4 border-b border-gray-100 flex flex-wrap gap-2">
            {events.by_kind.map((s) => (
              <span key={s.kind} className="text-xs px-2 py-1 bg-gray-100 border border-gray-200" data-testid={`stat-${s.kind}`}>
                <strong>{s.count}</strong> {s.kind}
              </span>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left">Cuando</th>
                <th className="px-3 py-2 text-left">Kind</th>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">IP hash</th>
                <th className="px-3 py-2 text-left">UA</th>
              </tr>
            </thead>
            <tbody>
              {(events?.items || []).map((e, i) => (
                <tr key={i} className="border-b border-gray-100" data-testid={`event-row-${i}`}>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(e.ts * 1000).toLocaleString("es-CO")}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-800">{e.kind}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{e.scope}</td>
                  <td className="px-3 py-2 text-xs font-mono">{e.email || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{e.ip_hash}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-gray-500 max-w-[250px] truncate" title={e.ua}>{e.ua || "—"}</td>
                </tr>
              ))}
              {(!events?.items || events.items.length === 0) && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-6">Sin eventos registrados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
